import React, { useState } from 'react';
import { FileCheck2, Menu, X, SearchCheck, ArrowRight } from 'lucide-react';
interface HeaderProps {
  onNavigate: (view: 'home' | 'guide' | 'pricing') => void;
}

const Header: React.FC<HeaderProps> = ({ onNavigate }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, view: 'home' | 'guide' | 'pricing', sectionId?: string) => {
    e.preventDefault();
    setIsMenuOpen(false);
    onNavigate(view);

    if (view === 'guide') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (sectionId === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (sectionId) {
      // Delay slightly to allow App.tsx to switch back to home if needed
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-primary/95 backdrop-blur-md">
      <div className="w-4/5 mx-auto flex h-20 items-center px-4 md:px-6 justify-between">
        <a
          className="mr-10 flex items-center space-x-3 cursor-pointer group"
          href="/"
          onClick={(e) => handleNavClick(e, 'home', 'top')}
        >
          <div className="text-accent group-hover:text-accent-hover transition-colors">
            <FileCheck2 className="h-10 w-10" />
          </div>
          <span className="text-2xl font-bold font-headline sm:inline-block text-white">Verifiko</span>
        </a>

        <nav className="hidden md:flex flex-1 items-center justify-center space-x-8 text-sm font-medium">
          <a
            className="text-slate-300 transition-colors hover:text-accent cursor-pointer"
            href="#services"
            onClick={(e) => handleNavClick(e, 'home', 'services')}
          >
            Usługi
          </a>
          <a
            className="text-slate-300 transition-colors hover:text-accent cursor-pointer"
            href="#about"
            onClick={(e) => handleNavClick(e, 'home', 'about')}
          >
            O Nas
          </a>
          <a
            className="text-slate-300 transition-colors hover:text-accent cursor-pointer"
            href="/cennik"
            onClick={(e) => handleNavClick(e, 'pricing')}
          >
            Cennik
          </a>
          <a
            className="text-slate-300 transition-colors hover:text-accent cursor-pointer"
            href="/przewodnik-po-reprezentacji-spolek-w-polsce"
            onClick={(e) => handleNavClick(e, 'guide')}
          >
            Poradnik
          </a>
          <a
            className="inline-flex items-center gap-1.5 text-accent transition-colors hover:text-accent-hover cursor-pointer"
            href="/sprawdz-firme"
            onClick={(e) => {
              e.preventDefault();
              setIsMenuOpen(false);
              onNavigate('home');
              window.history.pushState({ view: 'report', nip: '' }, '', '/sprawdz-firme');
              window.dispatchEvent(new PopStateEvent('popstate', { state: { view: 'report', nip: '' } }));
            }}
          >
            <SearchCheck className="w-4 h-4" />
            Sprawdź Spółkę
          </a>
        </nav>

        <div className="flex flex-1 items-center justify-end space-x-4">
          <div className="hidden items-center space-x-4 md:flex">
            <a
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-accent text-primary hover:bg-accent-hover h-10 px-6 py-2 cursor-pointer shadow-[0_0_15px_rgba(0,225,173,0.3)] hover:shadow-[0_0_20px_rgba(0,225,173,0.5)]"
              href={`${(import.meta as any).env.VITE_APP_URL || '__VITE_APP_URL_PLACEHOLDER__'}/register`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Zarejestruj się za darmo
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-white/20 bg-transparent text-white hover:bg-white/10 h-10 px-6 py-2"
              href={(import.meta as any).env.VITE_APP_URL || '__VITE_APP_URL_PLACEHOLDER__'}
            >
              Zaloguj się
              <ArrowRight className="w-4 h-4 opacity-70 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>

          <button
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-slate-300 hover:bg-white/10 hover:text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            <span className="sr-only">Toggle Menu</span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-white/10 bg-primary p-4 space-y-4 shadow-xl">
          <nav className="flex flex-col space-y-4">
            <a
              className="text-sm font-medium text-slate-300 hover:text-accent cursor-pointer"
              href="#services"
              onClick={(e) => handleNavClick(e, 'home', 'services')}
            >
              Usługi
            </a>
            <a
              className="text-sm font-medium text-slate-300 hover:text-accent cursor-pointer"
              href="#about"
              onClick={(e) => handleNavClick(e, 'home', 'about')}
            >
              O Nas
            </a>
            <a
              className="text-sm font-medium text-slate-300 hover:text-accent cursor-pointer"
              href="/cennik"
              onClick={(e) => handleNavClick(e, 'pricing')}
            >
              Cennik
            </a>
            <a
              className="text-sm font-medium text-slate-300 hover:text-accent cursor-pointer"
              href="/przewodnik-po-reprezentacji-spolek-w-polsce"
              onClick={(e) => handleNavClick(e, 'guide')}
            >
              Poradnik
            </a>
            <a
              className="text-sm font-medium text-accent hover:text-accent-hover cursor-pointer flex items-center gap-1.5"
              href="/sprawdz-firme"
              onClick={(e) => {
                e.preventDefault();
                setIsMenuOpen(false);
                window.history.pushState({ view: 'report', nip: '' }, '', '/sprawdz-firme');
                window.dispatchEvent(new PopStateEvent('popstate', { state: { view: 'report', nip: '' } }));
              }}
            >
              <SearchCheck className="w-4 h-4" />
              Sprawdź Spółkę
            </a>
          </nav>
          <div className="flex flex-col space-y-3 pt-4 border-t border-white/10">
            <a
              className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-bold bg-accent text-primary hover:bg-accent-hover h-10 px-4 py-2 cursor-pointer"
              href={`${(import.meta as any).env.VITE_APP_URL || '__VITE_APP_URL_PLACEHOLDER__'}/register`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Zarejestruj się za darmo
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              className="w-full inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-white/20 bg-transparent text-white hover:bg-white/10 h-10 px-4 py-2"
              href={(import.meta as any).env.VITE_APP_URL || '__VITE_APP_URL_PLACEHOLDER__'}
            >
              Zaloguj się
              <ArrowRight className="w-4 h-4 opacity-70" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;